// src/pages/ApplicationChampionshipMatches/components/MatchSetCurrentStageModal/index.tsx
import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Flex,
  Grid,
  InputGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import { SelectField } from '@/components/SelectField';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { GetParamsObject } from '@/model/query-params';
import { SeasonType } from '@/model';
import { useChampionships } from '@/services/queries/championship';
import { useSeasons, useSeasonById, usePutSeasonCurrentStageMutation } from '@/services/queries/season';
import { useStages } from '@/services/queries/stage';

const I18N_PREFIX = 'pages.application_championship_matches.components.match_current_stage_modal';

export interface MatchSetCurrentStageModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Chamado após salvar com sucesso. */
  onDone: () => void;
}

/**
 * Modal autossuficiente para definir a fase atual de uma temporada
 * (`seasons.current_stage_id`). Tem cascade próprio Campeonato → Temporada,
 * sem depender dos filtros da tabela. O select de Fase NÃO é um filtro: é o
 * próprio valor que está sendo gravado (qual fase o app destaca como "ao vivo").
 * Usa o endpoint dedicado PUT /seasons/:id/current-stage, que aceita apenas
 * `{ stage_uuid }`.
 */
export const MatchSetCurrentStageModal = ({ isOpen, onClose, onDone }: MatchSetCurrentStageModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const putCurrentStage = usePutSeasonCurrentStageMutation();

  const [championshipUuid, setChampionshipUuid] = useState('');
  const [seasonUuid, setSeasonUuid] = useState('');
  // Fase escolhida como atual (valor a ser gravado).
  const [stageUuid, setStageUuid] = useState('');

  // Cascade de lookups — mesmo padrão dos outros modais de Jogos.
  const championshipsParams = useMemo(
    () => GetParamsObject({} as Record<string, string>, 1, 200, 'asc', 'name'),
    [],
  );
  const { data: championshipsData } = useChampionships(championshipsParams);

  const seasonsParams = useMemo(
    () =>
      championshipUuid
        ? GetParamsObject({ championship_uuid: championshipUuid } as Record<string, string>, 1, 200, 'desc', 'year')
        : undefined,
    [championshipUuid],
  );
  const { data: seasonsData } = useSeasons(seasonsParams);

  // Fases da temporada — opções do select "fase atual".
  const stagesParams = useMemo(
    () =>
      seasonUuid
        ? GetParamsObject({ season_uuid: seasonUuid } as Record<string, string>, 1, 200, 'asc', 'stage_order')
        : undefined,
    [seasonUuid],
  );
  const { data: stagesData } = useStages(stagesParams);

  // Detalhe da temporada — fonte da current_stage_uuid para pré-preenchimento.
  const { data: selectedSeason } = useSeasonById(seasonUuid || '');

  // Sempre que a temporada muda, espelha a fase atual gravada no select.
  useEffect(() => {
    if (!seasonUuid) {
      setStageUuid('');
      return;
    }
    if (selectedSeason?.uuid === seasonUuid) {
      setStageUuid(selectedSeason.current_stage_uuid ?? '');
    }
  }, [seasonUuid, selectedSeason?.uuid, selectedSeason?.current_stage_uuid]);

  const resetAll = () => {
    setChampionshipUuid('');
    setSeasonUuid('');
    setStageUuid('');
  };

  const handleClose = () => {
    resetAll();
    onClose();
  };

  const handleChampionshipChange = (value: string) => {
    setChampionshipUuid(value);
    setSeasonUuid('');
    setStageUuid('');
  };

  const handleSeasonChange = (value: string) => {
    setSeasonUuid(value);
    setStageUuid('');
  };

  const currentStageLabel = useMemo(() => {
    const current = (stagesData?.data ?? []).find(stage => stage.uuid === selectedSeason?.current_stage_uuid);
    if (!current) return t(`${I18N_PREFIX}.current_value_empty`);
    const name = current.group_name ? `${current.name} · ${current.group_name}` : current.name;
    return t(`${I18N_PREFIX}.current_value`, { stage: name });
  }, [stagesData, selectedSeason?.current_stage_uuid, t]);

  const handleSave = () => {
    if (!seasonUuid || !stageUuid) return;

    putCurrentStage.mutate(
      { id: seasonUuid, payload: { stage_uuid: stageUuid } },
      {
        onSuccess: () => {
          toast({ status: 'success', title: t(`${I18N_PREFIX}.toasts.save_success`) });
          queryClient.invalidateQueries({ queryKey: ['GET_SEASON_BY_ID', seasonUuid] });
          resetAll();
          onDone();
        },
        onError: (error: unknown) => {
          toast({
            status: 'error',
            title: (error as Error)?.message || t(`${I18N_PREFIX}.toasts.save_error`),
          });
        },
      },
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='xl' isCentered scrollBehavior='inside'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex align='center' gap={2}>
            <ChakraFeatherIcon name='git-branch' color='brand.500' />
            <Text fontSize='medium' fontWeight='bold'>
              {t(`${I18N_PREFIX}.title`)}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Flex direction='column' gap={4}>
            <Text fontSize='sm' color='gray.600'>
              {t(`${I18N_PREFIX}.description`)}
            </Text>

            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_championship`)}</Text>
                <SelectField value={championshipUuid} onChange={event => handleChampionshipChange(event.target.value)}>
                  <option value=''>{t(`${I18N_PREFIX}.placeholder_select`)}</option>
                  {(championshipsData?.data ?? []).map(champ => (
                    <option key={champ.uuid} value={champ.uuid}>
                      {champ.name}
                    </option>
                  ))}
                </SelectField>
              </InputGroup>

              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_season`)}</Text>
                <SelectField
                  value={seasonUuid}
                  isDisabled={!championshipUuid}
                  onChange={event => handleSeasonChange(event.target.value)}
                >
                  <option value=''>{t(`${I18N_PREFIX}.placeholder_select`)}</option>
                  {(seasonsData?.data ?? []).map(season => (
                    <option key={season.uuid} value={season.uuid}>
                      {season.year}
                      {season.type === SeasonType.HYBRID ? ` · ${t(`${I18N_PREFIX}.season_type_hybrid`)}` : ''}
                      {season.type === SeasonType.KNOCKOUT ? ` · ${t(`${I18N_PREFIX}.season_type_knockout`)}` : ''}
                      {season.type === SeasonType.LEAGUE ? ` · ${t(`${I18N_PREFIX}.season_type_league`)}` : ''}
                    </option>
                  ))}
                </SelectField>
              </InputGroup>

              <InputGroup display='flex' flexDirection='column' gridColumn={{ md: '1 / -1' }}>
                <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_stage`)}</Text>
                <SelectField
                  value={stageUuid}
                  isDisabled={!seasonUuid}
                  onChange={event => setStageUuid(event.target.value)}
                >
                  <option value=''>{t(`${I18N_PREFIX}.placeholder_select`)}</option>
                  {(stagesData?.data ?? []).map(stage => (
                    <option key={stage.uuid} value={stage.uuid}>
                      {stage.group_name ? `${stage.name} · ${stage.group_name}` : stage.name}
                    </option>
                  ))}
                </SelectField>
              </InputGroup>
            </Grid>

            {seasonUuid ? (
              <Flex
                p={3}
                bg='gray.50'
                border='1px solid'
                borderColor='gray.200'
                borderRadius='md'
                align='center'
                gap={2}
              >
                <ChakraFeatherIcon name='info' boxSize='16px' color='gray.500' />
                <Text fontSize='sm' color='gray.600'>
                  {currentStageLabel}
                </Text>
              </Flex>
            ) : null}
          </Flex>
        </ModalBody>

        <ModalFooter>
          <Flex w='100%' justifyContent='flex-end' gap={3}>
            <Button variant='outline' color='gray.600' onClick={handleClose} isDisabled={putCurrentStage.isPending}>
              {t(`${I18N_PREFIX}.cancel_button`)}
            </Button>
            <Button
              bg='brand.500'
              color='white'
              _hover={{ bg: 'brand.600' }}
              px={10}
              onClick={handleSave}
              isLoading={putCurrentStage.isPending}
              isDisabled={!seasonUuid || !stageUuid}
            >
              {t(`${I18N_PREFIX}.save_button`)}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
